import React, { useState, useEffect, useRef } from 'react';
import { Line, Scatter } from 'react-chartjs-2';
import 'chart.js/auto';
import './Fire.css';
import vaultImage from './BFM/vault.png';
import { initializeApp, setLogLevel, getApps } from "firebase/app";
import { getStorage, ref, listAll, getDownloadURL } from "firebase/storage";
import AWS from 'aws-sdk';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: "dashboard-c2d5d",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

let app;
if (!getApps().length) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApps()[0]; // Use the already initialized app
}

// Initialize Firebase Storage
const storage = getStorage(app);

export { storage };

const Modal = ({ show, onClose, position, children }) => {
  if (!show) return null;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ top: position.top, left: position.left }}>
        <button className="close-button" onClick={onClose}>Close</button>
        <div className="modal-content">
          {children}
        </div>
      </div>
    </div>
  );
};

const Visualization = () => {
  const [vesselNames, setVesselNames] = useState([]);
  const [selectedVessel, setSelectedVessel] = useState('');
  const [folderContents, setFolderContents] = useState([]);
  const [temperatureData, setTemperatureData] = useState(null);
  const [salinityData, setSalinityData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedRange, setSelectedRange] = useState('All');
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const [speedData, setSpeedData] = useState(null);
  const [speedHeadingScatterData, setSpeedHeadingScatterData] = useState(null);
  const [portDurations, setPortDurations] = useState([]);
  const [error, setError] = useState('');
  const [csvData, setCsvData] = useState([]);
  const [showReadyMessage, setShowReadyMessage] = useState(false);
  const [vfxSoundOn, setVfxSoundOn] = useState(true); 
  const [track1SoundOn, setTrack1SoundOn] = useState(true); 
  const [settingsVisible, setSettingsVisible] = useState(false);
  const chooseAudioRef = useRef(null);
  const shotAudioRef = useRef(null);
  const track1AudioRef = useRef(null);
  const settingsRef = useRef(null);

  const s3 = new AWS.S3({
    accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
    region: process.env.REACT_APP_AWS_REGION,
  });

  useEffect(() => {
    const fetchCSVFromS3 = async () => {
      const params = {
        Bucket: 'git-dash-bucket', // Replace with your bucket name
        Key: 'new_VCSS Master 1.csv', // Replace with your file key
      };

      try {
        const data = await s3.getObject(params).promise();
        const csvContent = data.Body.toString('utf-8');
        parseCSV(csvContent);
      } catch (err) {
        console.error('Error fetching CSV file from S3:', err);
        setError('Error fetching CSV data from S3.');
        setTimeout(() => setError(''), 2000); // Clear the error after 2 seconds
      }
    };

    fetchCSVFromS3();
  }, []);

  useEffect(() => {
    // Play choose.mp3 on page load, then play track1.mp3 after choose.mp3 ends
    if (chooseAudioRef.current && track1AudioRef.current) {
      chooseAudioRef.current.play().then(() => {
        chooseAudioRef.current.addEventListener('ended', () => {
          if (track1SoundOn) {
            track1AudioRef.current.play().catch(error => {
              console.error('track1.mp3 play failed:', error);
            });
          }
        });
      }).catch(error => {
        console.error('choose.mp3 play failed:', error);
      });
    }
  }, []);  // Removed track1SoundOn dependency to prevent resetting on toggle

  const parseCSV = (csvContent) => {
    const lines = csvContent.split('\n').filter(line => line.trim() !== '');
    if (lines.length < 2) {
      console.error('CSV file is empty or improperly formatted.');
      return;
    }

    const headers = lines[0].split(',');
    const rows = lines.slice(1).map(line => {
      const values = line.split(',');
      return headers.reduce((acc, header, index) => {
        acc[header.trim()] = values[index] ? values[index].trim() : '';
        return acc;
      }, {});
    });

    setCsvData(rows);
    const uniqueVessels = [...new Set(rows.map(row => row.SHIPNAME).filter(name => name))];
    setVesselNames(uniqueVessels);
  };

  const handleVesselChange = (e) => {
    setSelectedVessel(e.target.value);

    if (vfxSoundOn && shotAudioRef.current) {
      shotAudioRef.current.play().catch(error => {
        console.error('shot.mp3 play failed:', error);
      });
    }

    setShowReadyMessage(true);
    setTimeout(() => {
      setShowReadyMessage(false);
    }, 1500);
  };

  const handleRangeChange = (e) => {
    setSelectedRange(e.target.value);
  };

  setLogLevel('debug');

  const handleVaultClick = async (e) => {
    if (!selectedVessel) {
      setError("Please select a vessel first.");
      setTimeout(() => setError(''), 2000); // Clear the error after 2 seconds
      return;
    }

    const folderPath = `${selectedVessel}/`; 
    console.log("Attempting to open folder:", folderPath);

    try {
      const folderRef = ref(storage, folderPath);
      const list = await listAll(folderRef);

      if (list.items.length > 0) {
        const fileUrls = [];
        for (const itemRef of list.items) {
          const url = await getDownloadURL(itemRef);
          fileUrls.push({ name: itemRef.name, url });
        }

        setFolderContents(fileUrls);

        const buttonRect = e.target.getBoundingClientRect();
        setModalPosition({
          top: buttonRect.bottom + window.scrollY + 'px',
          left: buttonRect.left + 'px'
        });

        setShowModal(true);
      } else {
        setError(`The vault is empty for vessel "${selectedVessel}".`);
        setTimeout(() => setError(''), 2000); // Clear the error after 2 seconds
      }
    } catch (err) {
      console.error("Error fetching vessel data:", err);
      setError("An error occurred while fetching vessel data.");
      setTimeout(() => setError(''), 2000); // Clear the error after 2 seconds
    }
  };

  const closeModal = () => setShowModal(false);

  const toggleVfxSound = () => {
    setVfxSoundOn(prevVfxSoundOn => {
      if (shotAudioRef.current) {
        if (prevVfxSoundOn) {
          shotAudioRef.current.pause();
        } else {
          shotAudioRef.current.play().catch(error => {
            console.error('shot.mp3 play failed:', error);
          });
        }
      }
      return !prevVfxSoundOn;
    });
  };

  const toggleTrack1Sound = () => {
    setTrack1SoundOn(prevTrack1SoundOn => {
      if (track1AudioRef.current) {
        if (prevTrack1SoundOn) {
          track1AudioRef.current.pause();
        } else {
          track1AudioRef.current.play().catch(error => {
            console.error('track1.mp3 play failed:', error);
          });
        }
      }
      return !prevTrack1SoundOn;
    });
  };

  const toggleSettingsVisibility = () => {
    setSettingsVisible(!settingsVisible);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setSettingsVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const calculatePortStayDurations = (data) => {
    const portDurations = [];
    let currentPort = null;
    let entryTime = null;
  
    data.forEach((row) => {
      const port = row.LAST_PORT;
      const time = new Date(row.LAST_PORT_TIME);
  
      if (currentPort === null) {
        currentPort = port;
        entryTime = time;
      } else if (port !== currentPort) {
        const duration = (time - entryTime) / (1000 * 60 * 60 * 24);
        const existingPort = portDurations.find(p => p.port === currentPort);
        if (existingPort) {
          existingPort.duration = parseFloat((parseFloat(existingPort.duration) + duration).toFixed(1));
        } else {
          portDurations.push({ port: currentPort, duration: duration.toFixed(1) });
        }
  
        currentPort = port;
        entryTime = time;
      }
    });
  
    if (currentPort !== null && entryTime !== null) {
      const finalTime = new Date(data[data.length - 1].LAST_PORT_TIME);
      const finalDuration = (finalTime - entryTime) / (1000 * 60 * 60 * 24);
  
      const existingPort = portDurations.find(p => p.port === currentPort);
      if (existingPort) {
        existingPort.duration = parseFloat((parseFloat(existingPort.duration) + finalDuration).toFixed(1));
      } else {
        portDurations.push({ port: currentPort, duration: finalDuration.toFixed(1) });
      }
    }
  
    return portDurations;
  };  

  useEffect(() => {
    if (selectedVessel) {
      const filteredData = csvData.filter(row => row.SHIPNAME === selectedVessel);

      console.log(`Filtered data for vessel ${selectedVessel}:`, filteredData);

      if (filteredData.length === 0) {
        console.warn('No data available for the selected vessel.');
        setTemperatureData(null);
        setSalinityData(null);
        setSpeedData(null);
        setSpeedHeadingScatterData(null);
        setPortDurations([]);
        return;
      }

      let dataRange = [];
      if (selectedRange === 'Month') {
        dataRange = filteredData.slice(-30);
      } else if (selectedRange === 'ThreeMonths') {
        dataRange = filteredData.slice(-90);
      } else {
        dataRange = filteredData;
      }

      console.log(`Data range for vessel ${selectedVessel} in range ${selectedRange}:`, dataRange);

      const timestamps = dataRange.map(row => row.DATE);
      const temperatures = dataRange.map(row => parseFloat(row.TEMPERATURE));
      const salinities = dataRange.map(row => parseFloat(row.SALINITY));
      const speeds = dataRange.map(row => parseFloat(row.SPEED));

      setTemperatureData({
        labels: timestamps,
        datasets: [{
          label: 'Temperature',
          data: temperatures,
          borderColor: 'orange',
          backgroundColor: 'rgba(255,99,132,0.2)',
          fill: true,
        }]
      });

      setSalinityData({
        labels: timestamps,
        datasets: [{
          label: 'Salinity',
          data: salinities,
          borderColor: 'blue',
          backgroundColor: 'rgba(54,162,235,0.2)',
          fill: true,
        }]
      });

      setSpeedData({
        labels: timestamps,
        datasets: [{
          label: 'Speed',
          data: speeds,
          borderColor: 'green',
          backgroundColor: 'rgba(75,192,192,0.2)',
          fill: true,
        }]
      });

      setSpeedHeadingScatterData({
        labels: timestamps,
        datasets: [{
          label: 'Speed vs Heading',
          data: dataRange.map(row => ({
            x: parseFloat(row.HEADING),
            y: parseFloat(row.SPEED),
          })),
          backgroundColor: 'rgba(255,99,132,0.5)',
        }]
      });

      const uniquePortDurations = calculatePortStayDurations(dataRange);
      
      console.log(`Port durations for vessel ${selectedVessel}:`, uniquePortDurations);
      
      setPortDurations(uniquePortDurations);
    } else {
      setTemperatureData(null);
      setSalinityData(null);
      setSpeedData(null);
      setSpeedHeadingScatterData(null);
      setPortDurations([]);
    }
  }, [selectedVessel, selectedRange, csvData]);

  return (
    <div className="visualization-container">
        <div className="dropdown-wrapper">
            <div className="dropdown-container">
                <div className="vessel-select">
                    <label htmlFor="vessel-select">Select your Player: </label>
                    <select id="vessel-select" value={selectedVessel} onChange={handleVesselChange}>
                        <option value="">Select a vessel</option>
                        {vesselNames.map(vessel => (
                            <option key={vessel} value={vessel}>
                                {vessel}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="time-range-select">
                    <label htmlFor="range-select">Select Data Range: </label>
                    <select id="range-select" value={selectedRange} onChange={handleRangeChange}>
                        <option value="All">All Data</option>
                        <option value="Month">Last Month</option>
                        <option value="ThreeMonths">Last 3 Months</option>
                    </select>
                </div>
            </div>
        </div>
        <audio ref={chooseAudioRef} src="/choose.mp3"></audio>
        <audio ref={shotAudioRef} src="/shot.mp3"></audio>
        <audio ref={track1AudioRef} src="/track1.mp3" loop></audio>
        {showReadyMessage && <div className="ready-message">Ready to Play?</div>}
        
        <button onClick={toggleSettingsVisibility} className="settings-button">
            Settings
        </button>
        {settingsVisible && (
            <div className="settings" ref={settingsRef}>
                <label>
                    VFX
                    <input type="checkbox" checked={vfxSoundOn} onChange={toggleVfxSound} />
                </label>
                <label>
                    Sound
                    <input type="checkbox" checked={track1SoundOn} onChange={toggleTrack1Sound} />
                </label> 
            </div>
        )}

        {error && <div className="error-message">Error: {error}</div>}

        {selectedVessel && (
            <>
                <div className="vessel-details-container">
                    <div className="details-card">
                        <h2>Vessel Details</h2>
                        <p><strong>Length:</strong> {parseFloat(csvData.find(row => row.SHIPNAME === selectedVessel).LENGTH).toFixed(2)} meters</p>
                        <p><strong>Build Year:</strong> {parseFloat(csvData.find(row => row.SHIPNAME === selectedVessel).YEAR_BUILT)} </p>
                        <p><strong>Type:</strong> {(csvData.find(row => row.SHIPNAME === selectedVessel).TYPE_NAME)} </p>
                        <p><strong>Flag:</strong> {(csvData.find(row => row.SHIPNAME === selectedVessel).FLAG)} </p>
                        <p><strong>Width:</strong> {parseFloat(csvData.find(row => row.SHIPNAME === selectedVessel).WIDTH).toFixed(2)} meters</p>
                        <p><strong>DWT:</strong> {parseFloat(csvData.find(row => row.SHIPNAME === selectedVessel).DWT).toFixed(2)} tons</p>
                    </div>
                    <div className="details-card port-list-card">
                        <h2>Visited Ports</h2>
                        <div className="port-list">
                            {portDurations.map(({ port, duration }, index) => (
                                <p key={`${port}-${index}`}>{port} - {duration} days</p>
                            ))}
                        </div>
                    </div>
                    
                    <div className="vault-button-container">
          <button className="vault-button" onClick={handleVaultClick} title="View Vessel Files">
            <img src={vaultImage} alt="Vault Icon" className="vault-icon" />
          </button>
        </div>
      </div>

      <Modal show={showModal} onClose={closeModal} position={modalPosition}>
        <h2>Files in {selectedVessel} Folder:</h2>
        <ul>
          {folderContents.map((file, index) => (
            <li key={index}>
              <a href={file.url} target="_blank" rel="noopener noreferrer">{file.name}</a>
            </li>
          ))}
        </ul>
      </Modal>
                <div className="charts-container">
                    {temperatureData && (
                        <div className="chart">
                            <h2>Temperature</h2>
                            <Line data={temperatureData} />
                        </div>
                    )}
                    {salinityData && (
                        <div className="chart">
                            <h2>Salinity</h2>
                            <Line data={salinityData} />
                        </div>
                    )}
                    {speedData && (
                        <div className="chart">
                            <h2>Speed</h2>
                            <Line data={speedData} />
                        </div>
                    )}
                    {speedHeadingScatterData && (
                        <div className="chart">
                            <h2>Speed vs Heading</h2>
                            <Scatter data={speedHeadingScatterData} />
                        </div>
                    )}
                </div>
            </>
        )}
    </div>
);
};

export default Visualization;
